import { useNavigate } from 'react-router-dom';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import {
  FileX,
  Database,
  Code,
  Question,
  type Icon,
} from '@phosphor-icons/react';

interface ErrorType {
  id: string;
  title: string;
  description: string;
  icon: Icon;
  suggestions: string[];
}

const errorTypes: ErrorType[] = [
  {
    id: 'file-format',
    title: 'Invalid File Format',
    description: 'The uploaded file does not match the expected format for gene expression data.',
    icon: FileX,
    suggestions: [
      'Ensure your file is in CSV, TSV, or TXT format',
      'Check that genes are in rows and samples are in columns',
      'Verify that the file contains numeric expression values',
      'Remove any special characters or formatting from the file'
    ]
  },
  {
    id: 'missing-metadata',
    title: 'Missing Metadata',
    description: 'Required sample metadata information is missing or incomplete.',
    icon: Database,
    suggestions: [
      'Include a header row with sample identifiers',
      'Ensure all samples have unique names',
      'Check for missing or empty columns'
    ]
  },
  {
    id: 'unsupported-genes',
    title: 'Unsupported Gene Identifiers',
    description: 'The gene identifiers in your data could not be matched to our reference database.',
    icon: Code,
    suggestions: [
      'Use standard gene identifiers (ENSEMBL IDs, HGNC symbols)',
      'Check for typos in gene names',
      'Ensure gene IDs are from human genome (currently supported)',
      'Consider converting your IDs using a gene ID mapping tool'
    ]
  },
  {
    id: 'empty-input',
    title: 'Empty or Invalid Input',
    description: 'The uploaded file appears to be empty or contains no valid data.',
    icon: Question,
    suggestions: [
      'Verify the file is not corrupted',
      'Check that the file contains at least 100 genes',
      'Ensure there are at least 3 samples in your dataset',
      'Try re-exporting your data from the source application'
    ]
  }
];

export function ErrorPage() {
  const navigate = useNavigate();
  
  // In a real app, this would come from URL params or state
  const currentError = errorTypes[0];
  const Icon = currentError.icon;

  return (
    <div className="min-h-screen bg-background pt-24 pb-12">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <Card className="p-8">
          <div className="flex items-start gap-4 mb-8">
            <div className="w-9 h-9 bg-muted rounded-lg flex items-center justify-center flex-shrink-0">
              <Icon className="w-4 h-4 text-muted-foreground" />
            </div>
            <div className="flex-1">
              <p className="font-mono text-[10px] tracking-[0.35em] text-muted-foreground/40 uppercase mb-2">
                Upload error
              </p>
              <h1 className="text-foreground mb-2">{currentError.title}</h1>
              <p className="text-muted-foreground">{currentError.description}</p>
            </div>
          </div>

          <div className="border border-border rounded-lg mb-8">
            <p className="px-5 pt-4 pb-2 font-mono text-[10px] tracking-[0.35em] text-muted-foreground/40 uppercase">
              How to fix this
            </p>
            <div className="divide-y divide-border">
              {currentError.suggestions.map((suggestion, idx) => (
                <div key={idx} className="flex items-start gap-4 px-5 py-3">
                  <span className="font-mono text-[10px] tracking-[0.2em] text-muted-foreground/30 mt-1 flex-shrink-0 w-4">
                    {String(idx + 1).padStart(2, '0')}
                  </span>
                  <span className="text-sm text-muted-foreground">{suggestion}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="flex gap-3">
            <Button variant="default" onClick={() => navigate('/upload')}>
              Try again
            </Button>
            <Button variant="secondary" onClick={() => window.location.href = 'mailto:hello@cellreveal.com'}>
              Contact support
            </Button>
          </div>
        </Card>

        {/* Common Issues */}
        <div className="mt-10">
          <p className="font-mono text-[10px] tracking-[0.35em] text-muted-foreground/40 uppercase mb-4">
            Common issues
          </p>
          <Card className="divide-y divide-border">
            {errorTypes.filter(e => e.id !== currentError.id).map(error => {
              const ErrorIcon = error.icon;
              return (
                <div key={error.id} className="flex items-start gap-3 p-5 hover:bg-muted/30 transition-colors">
                  <ErrorIcon className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="text-foreground mb-1">{error.title}</h4>
                    <p className="text-muted-foreground text-sm">{error.description}</p>
                  </div>
                </div>
              );
            })}
          </Card>
        </div>

        {/* Help footer */}
        <div className="mt-8 pt-6 border-t border-border">
          <p className="font-mono text-[10px] tracking-[0.35em] text-muted-foreground/40 uppercase mb-3">
            Still having issues?
          </p>
          <p className="text-sm text-muted-foreground mb-4">
            The documentation has detailed guides on data preparation and formatting. Reach out to support for personalized help.
          </p>
          <div className="flex gap-3">
            <Button variant="secondary">
              View documentation
            </Button>
            <Button variant="ghost">
              Contact support
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
